<template>
  <div class="order-number-container">
    <v-fade-transition>
      <div class="order-success-page">
        <!-- Success Icon -->
        <div class="success-icon-container">
          <div class="success-icon-circle">
            <v-icon color="success" size="36">mdi-check-circle</v-icon>
          </div>
        </div>

        <!-- Header -->
        <div class="order-header">
          <h1 class="amber--text text--lighten-3 font-weight-black">{{ $t("Your Order Number") }}</h1>
        </div>

        <!-- Animation -->
        <div class="animation-container">
          <lottie-animation
            v-if="showAnimation"
            ref="animation"
            :animationData="require('@/assets/wait.json')"
            :autoPlay="true"
          ></lottie-animation>
        </div>

        <!-- Order Number Display -->
        <div v-if="isLoading" class="skeleton-container">
          <v-skeleton-loader class="mx-auto" max-width="300" type="card"></v-skeleton-loader>
        </div>

        <div v-else class="order-number-display">
          <div class="order-number amber--text text--lighten-3">{{ orderNumber }}</div>
        </div>

        <!-- Order Details -->
        <div v-if="isLoading" class="skeleton-container">
          <v-skeleton-loader class="mx-auto" max-width="200" type="text"></v-skeleton-loader>
        </div>

        <div v-else class="order-details">
          <div class="detail-label">{{ $t("Total Amount") }}</div>
          <div class="detail-value">{{ orderAmount | priceDisplay }}</div>
        </div>

        <!-- Order Time -->
        <div v-if="isLoading" class="skeleton-container">
          <v-skeleton-loader class="mx-auto" max-width="150" type="text"></v-skeleton-loader>
        </div>

        <div v-else class="order-time">
          <v-icon small class="mr-1">mdi-clock-outline</v-icon>
          <span>{{ formattedOrderTime }}</span>
        </div>

        <!-- Info Message -->
        <div class="info-message">
          <v-icon class="mr-2">mdi-information-outline</v-icon>
          <span>{{ $t("Please show this number when picking up your order") }}</span>
        </div>

        <!-- Action Buttons -->
        <div class="action-buttons">
          <button class="primary-button" @click="showConfirmDialog = true">
            <v-icon color="black" left>mdi-check</v-icon>
            {{ $t("I've picked up my order") }}
          </button>
        </div>
      </div>
    </v-fade-transition>

    <!-- Confirmation Dialog -->
    <v-bottom-sheet v-model="showConfirmDialog">
      <v-list color="black">
        <v-list-item>
          <v-list-item-content>
            <v-list-item-title class="text-h6 mb-2">
              {{ $t("Confirm Pickup") }}
            </v-list-item-title>
            <v-list-item-subtitle>
              {{ $t("Are you sure you've picked up your order?") }}
            </v-list-item-subtitle>
          </v-list-item-content>
        </v-list-item>
        <v-list-item>
          <v-btn class="mt-2" color="primary black--text" width="100%" @click="orderPickedUp">
            <v-icon left>mdi-check</v-icon>
            {{ $t("Yes, I've picked it up") }}
          </v-btn>
        </v-list-item>
        <v-list-item>
          <v-btn class="my-2" color="grey lighten-5 black--text" width="100%" @click="showConfirmDialog = false">
            <v-icon left>mdi-close</v-icon>
            {{ $t("Cancel") }}
          </v-btn>
        </v-list-item>
      </v-list>
    </v-bottom-sheet>
  </div>
</template>

<script>
import dayjs from "dayjs";
import LottieAnimation from 'lottie-web-vue';

export default {
  name: "OrderNumberPage",
  components: {
    LottieAnimation
  },
  data() {
    return {
      orderNumber: "",
      orderAmount: 0,
      orderTimestamp: 0,
      showAnimation: true,
      isLoading: true,
      showConfirmDialog: false
    };
  },
  computed: {
    formattedOrderTime() {
      return dayjs(this.orderTimestamp).format("DD.MM.YYYY HH:mm");
    }
  },
  methods: {
    loadOrderInfo() {
      let orderInfo = null;
      try {
        orderInfo = JSON.parse(localStorage.getItem("paypalOrderInfo") || "null");
      } catch (e) {
        console.error("Error parsing paypalOrderInfo from localStorage:", e);
      }
      if (orderInfo) {
        this.orderNumber = orderInfo.orderNumber;
        this.orderAmount = orderInfo.orderAmount;
        this.orderTimestamp = orderInfo.timestamp;

        // Play animation and hide it after a delay
        this.$nextTick(() => {
          if (this.$refs.animation) {
            this.$refs.animation.play();
          }

          // Set loading to false after a short delay
          setTimeout(() => {
            this.isLoading = false;
          }, 500);

          // Hide animation after a longer delay
          setTimeout(() => {
            this.showAnimation = false;
          }, 2000);
        });
      } else {
        // If no order info, redirect to menu
        this.$router.replace("/menu");
      }
    },
    orderPickedUp() {
      // Clear the order info from localStorage
      localStorage.removeItem("paypalOrderInfo");

      // Close the confirmation dialog
      this.showConfirmDialog = false;

      // Redirect to menu page
      this.$router.replace("/menu");
    }
  },
  mounted() {
    this.loadOrderInfo();
  }
};
</script>

<style scoped>
.order-number-container {
  display: flex;
  justify-content: center;
  align-items: center;
  height: calc(100vh - 64px);
  padding: 16px;
  background: linear-gradient(135deg, #1a1a1a 0%, #121212 100%);
}

.order-success-page {
  position: relative;
  width: 100%;
  max-width: 500px;
  padding: 32px 24px;
  border-radius: 24px;
  background-color: #1e1e1e;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
  text-align: center;
}

.order-success-page::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 6px;
  background: linear-gradient(to right, #ffca28, #ffa000);
  border-radius: 24px 24px 0 0;
}

.success-icon-container {
  position: absolute;
  top: -20px;
  right: 24px;
  z-index: 2;
}

.success-icon-circle {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 48px;
  height: 48px;
  background-color: #121212;
  border-radius: 50%;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
}

.order-header {
  margin-bottom: 24px;
  padding-top: 8px;
}

.order-header h1 {
  font-size: 28px;
  margin: 0;
}

.animation-container {
  max-width: 120px;
  margin: 0 auto 16px;
}

.skeleton-container {
  margin: 24px 0;
}

.order-number-display {
  position: relative;
  margin: 32px 0;
  padding: 32px 16px;
  background-color: #212121;
  border-radius: 16px;
  overflow: hidden;
}

.order-number-display::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(45deg, rgba(255, 193, 7, 0.05), rgba(255, 193, 7, 0.1));
  z-index: 0;
}

.order-number-display::after {
  content: '';
  position: absolute;
  top: -50%;
  left: -50%;
  right: -50%;
  bottom: -50%;
  background: radial-gradient(circle, rgba(255, 193, 7, 0.1) 0%, rgba(0, 0, 0, 0) 70%);
  animation: rotate 10s linear infinite;
  z-index: 0;
}

.order-number {
  position: relative;
  font-size: 48px;
  font-weight: 900;
  animation: pulse 2s infinite;
  z-index: 1;
}

.order-details {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin: 24px 0;
}

.detail-label {
  font-size: 16px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.7);
  margin-bottom: 8px;
}

.detail-value {
  font-size: 24px;
  font-weight: 700;
}

.order-time {
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 16px 0 24px;
  color: rgba(255, 255, 255, 0.6);
  font-size: 14px;
}

.info-message {
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 24px 0;
  padding: 16px;
  background-color: rgba(33, 150, 243, 0.1);
  border-radius: 12px;
  border: 1px solid rgba(33, 150, 243, 0.2);
  color: rgba(255, 255, 255, 0.8);
}

.action-buttons {
  display: flex;
  flex-direction: column;
  margin-top: 32px;
}

.primary-button {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 48px;
  background-color: #ffca28;
  color: #000;
  border: none;
  border-radius: 24px;
  font-weight: 700;
  font-size: 16px;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 4px 12px rgba(255, 202, 40, 0.3);
}

.primary-button:hover {
  background-color: #ffd54f;
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(255, 202, 40, 0.4);
}

.secondary-button {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 48px;
  background-color: transparent;
  color: rgba(255, 255, 255, 0.7);
  border: none;
  border-radius: 24px;
  font-weight: 500;
  font-size: 16px;
  margin-top: 16px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.secondary-button:hover {
  color: rgba(255, 255, 255, 0.9);
  background-color: rgba(255, 255, 255, 0.05);
}

@keyframes rotate {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

@keyframes pulse {
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
  100% {
    transform: scale(1);
  }
}

/* Responsive adjustments */
@media (max-width: 600px) {
  .order-success-page {
    padding: 24px 16px;
  }

  .order-header h1 {
    font-size: 24px;
  }

  .order-number {
    font-size: 36px;
  }

  .detail-value {
    font-size: 20px;
  }

  .order-number-display {
    padding: 24px 16px;
  }
}
</style>
